<template>
  <div class="envio-contabilidade-page">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Envio automático para contabilidade
          </span>
          <span class="text-muted mt-3 font-weight-bold font-size-sm">
            Configure a rotina mensal, acompanhe o Histórico e baixe o Pacote ZIP.
          </span>
        </h3>
        <div class="card-toolbar">
          <button
            type="button"
            class="btn btn-light mr-2"
            :disabled="loading"
            @click="carregarTudo"
          >
            <i class="fa fa-sync mr-1"></i>
            Atualizar
          </button>
          <button type="button" class="btn btn-primary" @click="novaConfiguracao">
            <i class="fa fa-plus mr-1"></i>
            Nova configuração
          </button>
        </div>
      </div>
    </div>

    <b-tabs v-model="activeTab" content-class="mt-4">
      <b-tab title="Configurações">
        <div class="card card-custom gutter-b">
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover table-striped">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Destinatários</th>
                    <th>Rotina</th>
                    <th>Período</th>
                    <th>Próxima execução</th>
                    <th>Última execução</th>
                    <th class="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="configuracao in configuracoes" :key="configuracao.id">
                    <td>
                      <strong>{{ configuracao.nome }}</strong>
                    </td>
                    <td>
                      <b-badge :variant="configuracao.ativo ? 'success' : 'secondary'">
                        {{ configuracao.ativo ? "Ativo" : "Inativo" }}
                      </b-badge>
                    </td>
                    <td>{{ listaEmails(configuracao.emails) }}</td>
                    <td>
                      Dia {{ configuracao.dia_mes }}: gera 00:00-06:00 e envia às 07:00
                    </td>
                    <td>{{ labelPeriodo(configuracao.periodo) }}</td>
                    <td>{{ formatDateTime(configuracao.proxima_execucao) }}</td>
                    <td>{{ formatDateTime(configuracao.ultima_execucao) }}</td>
                    <td class="text-right text-nowrap">
                      <button
                        type="button"
                        class="btn btn-sm btn-light-primary btn-icon mr-2"
                        v-b-tooltip.hover.bottom="'Editar'"
                        @click="editarConfiguracao(configuracao)"
                      >
                        <i class="fa fa-pen"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-light-info btn-icon mr-2"
                        v-b-tooltip.hover.bottom="'Enviar teste'"
                        @click="enviarTeste(configuracao)"
                      >
                        <i class="fa fa-paper-plane"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-light btn-icon mr-2"
                        v-b-tooltip.hover.bottom="'Histórico'"
                        @click="verHistorico(configuracao)"
                      >
                        <i class="fa fa-history"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-icon mr-2"
                        :class="
                          configuracao.ativo
                            ? 'btn-light-warning'
                            : 'btn-light-success'
                        "
                        v-b-tooltip.hover.bottom="
                          configuracao.ativo ? 'Desativar' : 'Ativar'
                        "
                        @click="alterarStatusConfiguracao(configuracao)"
                      >
                        <i
                          :class="
                            configuracao.ativo ? 'fa fa-ban' : 'fa fa-check'
                          "
                        ></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-light-danger btn-icon"
                        v-b-tooltip.hover.bottom="'Excluir'"
                        @click="excluirConfiguracao(configuracao)"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!loading && configuracoes.length === 0">
                    <td colspan="8" class="text-center text-muted py-8">
                      Nenhuma configuração encontrada.
                    </td>
                  </tr>
                  <tr v-if="loading">
                    <td colspan="8" class="text-center text-muted py-8">
                      Carregando configurações...
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab title="Histórico">
        <div class="card card-custom gutter-b">
          <div class="card-header border-0 py-4">
            <h3 class="card-title align-items-start flex-column">
              <span class="card-label font-weight-bolder text-dark">
                Histórico
              </span>
              <span v-if="configuracaoHistorico" class="text-muted mt-2">
                Configuração: {{ configuracaoHistorico.nome }}
              </span>
            </h3>
            <div class="card-toolbar">
              <button
                v-if="configuracaoHistorico"
                type="button"
                class="btn btn-light mr-2"
                @click="limparFiltroHistorico"
              >
                Limpar filtro
              </button>
              <button
                type="button"
                class="btn btn-light-primary"
                :disabled="loadingExecucoes"
                @click="carregarExecucoes"
              >
                <i class="fa fa-sync mr-1"></i>
                Atualizar Histórico
              </button>
            </div>
          </div>

          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover table-striped">
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>Período</th>
                    <th>Tipo de execução</th>
                    <th>Status</th>
                    <th>Início</th>
                    <th>Fim</th>
                    <th>Tamanho do pacote</th>
                    <th class="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="execucao in execucoes" :key="execucao.id">
                    <td>{{ execucao.competencia }}</td>
                    <td>{{ formatPeriodo(execucao.data_inicial, execucao.data_final) }}</td>
                    <td>{{ labelTipoExecucao(execucao.tipo_execucao) }}</td>
                    <td><StatusBadge :status="execucao.status" /></td>
                    <td>{{ formatDateTime(execucao.iniciado_em) }}</td>
                    <td>{{ formatDateTime(execucao.finalizado_em) }}</td>
                    <td>{{ formatBytes(execucao.zip_size) }}</td>
                    <td class="text-right text-nowrap">
                      <button
                        type="button"
                        class="btn btn-sm btn-light-primary btn-icon mr-2"
                        :disabled="!temPacote(execucao)"
                        v-b-tooltip.hover.bottom="'Baixar pacote'"
                        @click="baixarPacote(execucao)"
                      >
                        <i class="fa fa-download"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-light btn-icon mr-2"
                        :disabled="!temPacote(execucao)"
                        v-b-tooltip.hover.bottom="'Reenviar e-mail'"
                        @click="reenviar(execucao)"
                      >
                        <i class="fa fa-envelope"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-light-info btn-icon"
                        v-b-tooltip.hover.bottom="'Detalhes'"
                        @click="detalharExecucao(execucao)"
                      >
                        <i class="fa fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!loadingExecucoes && execucoes.length === 0">
                    <td colspan="8" class="text-center text-muted py-8">
                      Nenhuma execução encontrada.
                    </td>
                  </tr>
                  <tr v-if="loadingExecucoes">
                    <td colspan="8" class="text-center text-muted py-8">
                      Carregando Histórico...
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <ConfigFormModal
      :show="showConfigModal"
      :value="configuracaoSelecionada"
      :itens="itens"
      :saving="savingConfig"
      @close="fecharConfigModal"
      @save="salvarConfiguracao"
    />

    <ExecucaoDetalheModal
      :show="showDetalheModal"
      :execucao="execucaoSelecionada"
      @close="fecharDetalheModal"
      @download="baixarPacote"
      @resend="reenviar"
    />
  </div>
</template>

<script>
import ConfigFormModal from "../components/ConfigFormModal.vue";
import ExecucaoDetalheModal from "../components/ExecucaoDetalheModal.vue";
import StatusBadge from "../components/StatusBadge.vue";
import envioContabilidadeApi from "../api";
import {
  formatBytes,
  formatDateTime,
  formatPeriodo,
  labelPeriodo,
  labelTipoExecucao,
  normalizeCollection,
} from "@/modules/envio-contabilidade/utils";

export default {
  name: "EnvioContabilidadeIndex",
  components: {
    ConfigFormModal,
    ExecucaoDetalheModal,
    StatusBadge,
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      loadingExecucoes: false,
      savingConfig: false,
      configuracoes: [],
      execucoes: [],
      itens: [],
      configuracaoSelecionada: null,
      configuracaoHistorico: null,
      execucaoSelecionada: null,
      showConfigModal: false,
      showDetalheModal: false,
    };
  },
  mounted() {
    this.carregarTudo();
  },
  methods: {
    formatBytes,
    formatDateTime,
    formatPeriodo,
    labelPeriodo,
    labelTipoExecucao,
    async carregarTudo() {
      await Promise.all([
        this.carregarItens(),
        this.carregarConfiguracoes(),
        this.carregarExecucoes(),
      ]);
    },
    async carregarItens() {
      try {
        const payload = await envioContabilidadeApi.listarItens();
        this.itens = normalizeCollection(payload);
      } catch (error) {
        this.notificarErro("Não foi possível carregar os itens para envio.");
      }
    },
    async carregarConfiguracoes() {
      this.loading = true;

      try {
        const payload = await envioContabilidadeApi.listarConfiguracoes();
        this.configuracoes = normalizeCollection(payload);
      } catch (error) {
        this.notificarErro("Não foi possível carregar as configurações.");
      } finally {
        this.loading = false;
      }
    },
    async carregarExecucoes() {
      this.loadingExecucoes = true;

      try {
        const params = {};

        if (this.configuracaoHistorico) {
          params.config_id = this.configuracaoHistorico.id;
        }

        const payload = await envioContabilidadeApi.listarExecucoes(params);
        this.execucoes = normalizeCollection(payload);
      } catch (error) {
        this.notificarErro("Não foi possível carregar o Histórico.");
      } finally {
        this.loadingExecucoes = false;
      }
    },
    novaConfiguracao() {
      this.configuracaoSelecionada = null;
      this.showConfigModal = true;
    },
    editarConfiguracao(configuracao) {
      this.configuracaoSelecionada = { ...configuracao };
      this.showConfigModal = true;
    },
    fecharConfigModal() {
      this.showConfigModal = false;
      this.configuracaoSelecionada = null;
    },
    async salvarConfiguracao(payload) {
      this.savingConfig = true;

      try {
        if (this.configuracaoSelecionada && this.configuracaoSelecionada.id) {
          await envioContabilidadeApi.editarConfiguracao(
            this.configuracaoSelecionada.id,
            payload
          );
          this.notificarSucesso("Configuração salva com sucesso.");
        } else {
          await envioContabilidadeApi.criarConfiguracao(payload);
          this.notificarSucesso("Configuração salva com sucesso.");
        }

        this.fecharConfigModal();
        await this.carregarConfiguracoes();
      } catch (error) {
        this.notificarErro("Não foi possível salvar a configuração.");
      } finally {
        this.savingConfig = false;
      }
    },
    async executarAgora(configuracao) {
      if (!window.confirm("Deseja enviar esta configuração para a fila agora? Use apenas para testes locais.")) {
        return;
      }

      try {
        await envioContabilidadeApi.executarAgora(configuracao.id);
        this.notificarSucesso("Configuração enviada para a fila agora.");
        await this.carregarExecucoes();
      } catch (error) {
        this.notificarErro("Não foi possível enviar para a fila agora.");
      }
    },
    async enviarTeste(configuracao) {
      if (!window.confirm("Deseja enviar um teste desta configuração?")) {
        return;
      }

      try {
        await envioContabilidadeApi.enviarTeste(configuracao.id);
        this.notificarSucesso("Envio de teste enviado para a fila.");
        await this.carregarExecucoes();
      } catch (error) {
        this.notificarErro("Não foi possível enviar o teste.");
      }
    },
    async alterarStatusConfiguracao(configuracao) {
      const proximoStatus = !configuracao.ativo;
      const acao = proximoStatus ? "ativar" : "desativar";

      if (!window.confirm(`Deseja ${acao} esta configuração?`)) {
        return;
      }

      try {
        await envioContabilidadeApi.alterarStatusConfiguracao(
          configuracao,
          proximoStatus
        );
        this.notificarSucesso(
          `Configuração ${
            proximoStatus ? "ativada" : "desativada"
          } com sucesso.`
        );
        await this.carregarConfiguracoes();
      } catch (error) {
        this.notificarErro(`Não foi possível ${acao} a configuração.`);
      }
    },
    async excluirConfiguracao(configuracao) {
      if (!window.confirm("Deseja excluir esta configuração?")) {
        return;
      }

      try {
        await envioContabilidadeApi.excluirConfiguracao(configuracao.id);
        this.notificarSucesso("Configuração removida com sucesso.");
        await this.carregarConfiguracoes();
      } catch (error) {
        this.notificarErro("Não foi possível excluir a configuração.");
      }
    },
    async verHistorico(configuracao) {
      this.configuracaoHistorico = configuracao;
      this.activeTab = 1;
      await this.carregarExecucoes();
    },
    async limparFiltroHistorico() {
      this.configuracaoHistorico = null;
      await this.carregarExecucoes();
    },
    async detalharExecucao(execucao) {
      try {
        const payload = await envioContabilidadeApi.detalharExecucao(execucao.id);
        this.execucaoSelecionada = payload.data || payload;
        this.showDetalheModal = true;
      } catch (error) {
        this.notificarErro("Não foi possível carregar o detalhe da execução.");
      }
    },
    fecharDetalheModal() {
      this.showDetalheModal = false;
      this.execucaoSelecionada = null;
    },
    async baixarPacote(execucao) {
      try {
        const payload = await envioContabilidadeApi.baixarPacote(execucao.id);
        const url = payload.url || (payload.data && payload.data.url);

        if (!url) {
          this.notificarErro("Não foi possível baixar o Pacote ZIP.");
          return;
        }

        window.open(url, "_blank");
      } catch (error) {
        this.notificarErro("Não foi possível baixar o Pacote ZIP.");
      }
    },
    async reenviar(execucao) {
      if (!window.confirm("Deseja reenviar o e-mail desta execução?")) {
        return;
      }

      try {
        await envioContabilidadeApi.reenviar(execucao.id);
        this.notificarSucesso("Reenvio enviado para a fila.");
        await this.carregarExecucoes();
      } catch (error) {
        this.notificarErro("Não foi possível reenviar o e-mail.");
      }
    },
    temPacote(execucao) {
      return Boolean(execucao && (execucao.zip_url || execucao.zip_path));
    },
    listaEmails(emails) {
      return Array.isArray(emails) && emails.length > 0 ? emails.join(", ") : "-";
    },
    notificarSucesso(message) {
      if (this.$toast) {
        this.$toast.success(message);
      }
    },
    notificarErro(message) {
      if (this.$toast) {
        this.$toast.error(message);
      }
    },
  },
};
</script>

<style scoped>
.envio-contabilidade-page .btn-icon {
  height: 32px;
  width: 32px;
  padding: 0;
}

.envio-contabilidade-page .table td,
.envio-contabilidade-page .table th {
  vertical-align: middle;
}
</style>
